<script setup>
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorField from '../../fields/ColorField.vue';
import SpacingField from '../../fields/SpacingField.vue';
import SliderField from '../../fields/SliderField.vue';
import FontField from '../../fields/FontField.vue';
import AlignField from '../../fields/AlignField.vue';
import VisibilityField from '../../fields/VisibilityField.vue';

const props = defineProps({
  node: { type: Object, required: true },
  // Elemento do rodapé fica sempre visível (o servidor força): sem o campo.
  selection: { type: Object, default: null },
});
const emit = defineEmits(['update']);
const ed = inject('emailEditor');
const { t } = useI18n();
const theme = computed(() => ed.template.value?.theme || {});
const fonts = computed(() => ed.catalog.value?.layout?.fonts);
const heading = computed(() => props.node.type === 'heading');
const st = computed(() => props.node.style || {});
const style = (key, value) => emit('update', { style: { [key]: value } }, key);
</script>

<template>
  <div data-props="text" class="flex flex-col gap-4">
    <p class="rounded-lg bg-n-brand/10 px-3 py-2 text-xs text-n-brand">
      {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.EDIT_HINT') }}
    </p>
    <label v-if="heading" class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PROPS.LEVEL')
      }}</span>
      <select
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        :value="node.level"
        @change="
          emit('update', { level: Number($event.target.value) }, 'level')
        "
      >
        <option v-for="l in [1, 2, 3]" :key="l" :value="l">
          {{ `H${l}` }}
        </option>
      </select>
    </label>
    <FontField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.FONT')"
      :fonts="fonts"
      :model-value="st.font_family"
      @update:model-value="v => style('font_family', v)"
    />
    <div data-field="font_size">
      <SliderField
        :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.SIZE')"
        :model-value="st.font_size"
        :min="8"
        :max="72"
        unit="px"
        @update:model-value="v => style('font_size', v)"
      />
    </div>
    <label v-if="heading" class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PROPS.WEIGHT')
      }}</span>
      <select
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        :value="st.font_weight"
        @change="style('font_weight', Number($event.target.value))"
      >
        <option v-for="w in [400, 600, 700]" :key="w" :value="w">
          {{ t(`EMAIL_TEMPLATES.EDITOR.PROPS.WEIGHTS.${w}`) }}
        </option>
      </select>
    </label>
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.LINE_HEIGHT')"
      :model-value="st.line_height"
      :min="1"
      :max="3"
      :step="0.05"
      @update:model-value="v => style('line_height', v)"
    />
    <ColorField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.COLOR')"
      :theme="theme"
      allow-auto
      :model-value="st.color"
      @update:model-value="v => style('color', v)"
    />
    <AlignField
      :model-value="st.align"
      @update:model-value="v => style('align', v)"
    />
    <SpacingField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.PADDING')"
      :model-value="st.padding"
      @update:model-value="v => style('padding', v)"
    />
    <VisibilityField
      v-if="selection?.row?.role !== 'footer'"
      :model-value="st.hide_on"
      @update:model-value="v => style('hide_on', v)"
    />
  </div>
</template>
